import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react'
import {
  getConsentCookie,
  setConsentCookie,
  getOrCreateAnonymousId,
  isConsentSynced,
  markConsentSynced,
} from '../utils/cookieConsent'
import { getStoredToken } from '../utils/auth'
import { useAuthState } from './AuthContext'

type ConsentAction = 'all' | 'necessary' | 'custom'

interface CookiePreferences {
  necessary: boolean
  analytics: boolean
}

interface CookieConsentState {
  ready: boolean
  hasConsented: boolean
  analyticsAllowed: boolean
  preferences: CookiePreferences
  preferencesOpen: boolean
  acceptAll: () => void
  declineAll: () => void
  savePreferences: (prefs: { analytics: boolean }) => void
  openPreferences: () => void
  closePreferences: () => void
}

const CookieConsentContext = createContext<CookieConsentState | null>(null)

function postConsentToBackend(prefs: CookiePreferences, action: ConsentAction) {
  const anonymousId = getOrCreateAnonymousId()
  const baseUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/v1'

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = getStoredToken()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  fetch(`${baseUrl}/cookie-consent`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      anonymousId,
      preferences: prefs,
      consentAction: action,
      source: 'app',
    }),
  }).catch(err => console.warn('Failed to record consent:', err))
}

export function CookieConsentProvider({ children }: { children: React.ReactNode }) {
  const [consent, setConsent] = useState<'all' | 'necessary' | null>(null)
  const [ready, setReady] = useState(false)
  const [preferencesOpen, setPreferencesOpen] = useState(false)

  useEffect(() => {
    const existing = getConsentCookie()
    setConsent(existing)
    setReady(true)

    if (existing && !isConsentSynced()) {
      const action = existing === 'all' ? 'all' : 'necessary'
      postConsentToBackend({ necessary: true, analytics: existing === 'all' }, action)
      markConsentSynced()
    }
  }, [])

  const auth = useAuthState()

  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user?.id) return

    const anonymousId = getOrCreateAnonymousId()
    const linkKey = `${anonymousId}:${auth.user.id}`
    if (localStorage.getItem('solva_consent_linked') === linkKey) return

    const token = getStoredToken()
    if (!token) return

    const baseUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/v1'
    fetch(`${baseUrl}/cookie-consent/link`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ anonymousId }),
    })
      .then(res => {
        if (res.ok) localStorage.setItem('solva_consent_linked', linkKey)
        else console.warn('Failed to link consent:', res.status, res.statusText)
      })
      .catch(err => console.warn('Failed to link consent:', err))
  }, [auth.isAuthenticated, auth.user?.id])

  const hasConsented = consent !== null
  const analyticsAllowed = consent === 'all'
  const preferences = useMemo<CookiePreferences>(
    () => ({ necessary: true, analytics: analyticsAllowed }),
    [analyticsAllowed],
  )

  const acceptAll = useCallback(() => {
    setConsentCookie('all')
    setConsent('all')
    postConsentToBackend({ necessary: true, analytics: true }, 'all')
    markConsentSynced()
  }, [])

  const declineAll = useCallback(() => {
    setConsentCookie('necessary')
    setConsent('necessary')
    postConsentToBackend({ necessary: true, analytics: false }, 'necessary')
    markConsentSynced()
  }, [])

  const savePreferences = useCallback((prefs: { analytics: boolean }) => {
    const value = prefs.analytics ? 'all' : 'necessary'
    setConsentCookie(value)
    setConsent(value)
    setPreferencesOpen(false)
    postConsentToBackend({ necessary: true, analytics: prefs.analytics }, 'custom')
    markConsentSynced()
  }, [])

  const openPreferences = useCallback(() => setPreferencesOpen(true), [])
  const closePreferences = useCallback(() => setPreferencesOpen(false), [])

  const value = useMemo<CookieConsentState>(
    () => ({
      ready,
      hasConsented,
      analyticsAllowed,
      preferences,
      preferencesOpen,
      acceptAll,
      declineAll,
      savePreferences,
      openPreferences,
      closePreferences,
    }),
    [
      ready,
      hasConsented,
      analyticsAllowed,
      preferences,
      preferencesOpen,
      acceptAll,
      declineAll,
      savePreferences,
      openPreferences,
      closePreferences,
    ],
  )

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>
}

export function useCookieConsent(): CookieConsentState {
  const context = useContext(CookieConsentContext)
  if (!context) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider')
  }
  return context
}
